import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import useChart from "../../api/mutations/homepageChart";
import useTestMutation from "../../api/mutations/testMutation";
import { useGetTestResults } from "../../api/queries/test.queries";
import downloadIcon from "../../assets/download.svg";
import circle104 from "../../assets/elipse104.svg";
import circle106 from "../../assets/elipse106.svg";
import circle107 from "../../assets/elipse107.svg";
import circle108 from "../../assets/elipse108.svg";
import circle from "../../assets/enter-code-circle.svg";
import printIcon from "../../assets/print.svg";
import CustomSelect from "../../components/CustomSelect";
import { CustomSpinner } from "../../components/CustomSpinner";
import DynamicChart from "../../components/DynamicChart";
import HurSection from "../../components/HurSection";
import { CHART_ERROR_MSG, SELECT_OPTIONS } from "../HomePage/HomePage.const";
import ErrorPage from "../TestPage/ErrorTestPage";
type ChartExplanationItem = {
  reason: string;
  percent: number;
};

const TEXT = {
  HEADER: "Ditt testresultat - ",
};

// Text constants
const TEXTS = {
  TITLE:
    "Skönmålning – Värdeförändring – motivation – empati - stresshantering",
  PARAGRAPH_1:
    "Skönmålning påverkar hur du exempelvis överdriver värdet på dina investeringar. Skönmålning får dig att undvika att förstå hur du ibland kan uppleva jobbiga känslor eller ”sann” verklighet för att känna dig bättre.",
  HEADER_MOTIVATION: "Skönmålning och Motivation",
  PARAGRAPH_2:
    "När dina investeringar stiger mycket i värde känner du dig ofta stolt och glad. Skönmålning kan få dig att tro att du är riktigt skicklig på att hantera marknaden eller mer problematiskt - att marknaden alltid kommer att vara på din sida. Detta gör att du kan börja att ta större risker utan att tänka på utfallet. Med hög EQ-motivation påminner du dig själv om att framgång är bra, men att du behöver vara realistisk och hålla fast vid dina långsiktiga mål.",
  PARAGRAPH_3:
    "När värdet på dina investeringar sjunker kan du ibland försöka intala dig själv att det inte är så illa, för att slippa att känna dig misslyckad. Du kanske säger till dig själv att “förlusten bara är tillfällig” eller helt enkelt blundar för problemet. Men med hög EQ-motivation accepterar du verkligheten, lär dig successivt av dina misstag och fortsätter kämpa mot dina mål utan att fly från realiteter.",
  HEADER_EMPATHY: "Skönmålning och Empati",
  PARAGRAPH_4:
    "När du berättar om en stor vinst för din familj eller partner kan skönmålning få dig att överdriva hur bra det gått vilket skapar orealistiska förväntningar. Om värdet sedan sjunker snabbt kan det skapa besvikelse och problem. Med av din empati är du ärlig och balanserad när du pratar om framgångar vilket minskar risken för att bygga upp för stora förhoppningar.",
  PARAGRAPH_5:
    "Om dina investeringar minskar i värde kan du försöka skydda dina nära från oro genom att låtsas att det inte är så farligt. Det bättre att vara ärlig och lyssna på deras oro utan att överdriva eller tona ner allvaret. Det skapar en trygg känsla och gör att ni kan ta gemensamma beslut med förtroende.",
  HEADER_STRESS: "Skönmålning och Stresshantering",
  PARAGRAPH_6:
    "När du gör stora vinster kan du känna press att fortsätta fatta “rätt beslut”. Skönmålning kan få dig att tro att allt alltid kommer gå bra, vilket kan göra att du blir oförsiktig. Med hög stresshantering håller du dig lugn och ser på situationen med öppna ögon, så att du inte låter framgången stressa dig.",
  PARAGRAPH_7:
    "Om dina investeringar tappar värde kan du känna mycket stress och vilja blunda för verkligheten. Skönmålning kan få dig att förneka hur allvarligt det är och skjuta upp jobbiga beslut. Med hög EQ-stresshantering vågar du möta verkligheten, lugna dina känslor och tänka igenom dina val utan att låtsas som att allt är bra.",
  SUMMARY_HEADER: "Sammanfattning",
  SUMMARY:
    "Skönmålning är ett sätt att slippa jobbiga känslor genom att ”passa in” socialt i hur du ser på dina investeringar, både vid uppgångar och nedgångar. Med hjälp av din EQ behöver du inte skönmåla lika mycket eftersom du hanterar motivation, empati och stress på ett balanserat sätt. Du ser verkligheten tydligare, hanterar dina känslor på ett bra sätt och är ärlig mot dig själv och din omgivning. Det gör att du kan fatta smartare och mer hållbara beslut för framtiden.",
};

// Text constants for EQ and Decision insights
const EQ_TEXTS = {
  TITLE: "Hur kan du använda de insikter som ditt testresultat har givit dig?",
  HEADER_DECISION: "EQ - Beslut",
  INTRODUCTION:
    "Emotionell intelligens hjälper dig att fatta bättre beslut genom att stärka din självmedvetenhet, självkontroll, empati, motivation, sociala färdigheter och din förmåga att hantera stress. Tillsammans stärker denna kombination dina beslut och balanserar din förmåga att fatta bättre beslut:",
  SECTION_SELF_AWARENESS: {
    HEADER: "Självmedvetenhet",
    TEXT: "Med EQ förstår du dina egna känslor, styrkor och svagheter. Du märker när känslor som rädsla, ilska eller överdriven självsäkerhet börjar påverka ditt tänkande. Genom att vara medveten om det kan du undvika att fatta impulsiva eller ogenomtänkta beslut.",
  },
  SECTION_SELF_REGULATION: {
    HEADER: "Självreglering",
    TEXT: "Med hög EQ kan du kontrollera dina känslomässiga reaktioner, särskilt när du är stressad eller är under press. Du försöker hålla dig lugn och fokuserad, så att du kan tänka klart och fatta beslut som är genomtänkta och logiska, i stället för att reagera snabbt och ångra dig senare.",
  },
  SECTION_EMPATHY: {
    HEADER: "Empati",
    TEXT: "Empati gör att du kan förstå hur andra känner och vad de behöver. När du fattar beslut som påverkar andra, hjälper empatin dig att tänka på deras perspektiv och ta hänsyn till deras känslor. Det är särskilt viktigt när du samarbetar med andra, eftersom det hjälper er att komma fram till lösningar som fungerar för alla och skapar bättre samarbete.",
  },
  SECTION_MOTIVATION: {
    HEADER: "Motivation",
    TEXT: "Motivation hjälper dig att fatta beslut som stämmer överens med vad du verkligen vill i stället för att bara gå på kortsiktiga impulser. Du ger inte upp när det blir svårt utan kämpar vidare för att nå dina mål.",
  },
  SECTION_SOCIAL_SKILLS: {
    HEADER: "Sociala färdigheter",
    TEXT: "Goda sociala färdigheter gör att du kan kommunicera dina beslut tydligt, få stöd från andra och samarbeta bättre. EQ hjälper dig att presentera dina idéer, lösa konflikter och lyssna på andras synpunkter. Det gör att ni tillsammans kan hitta lösningar som fler vill stötta och som fungerar för alla.",
  },
  SECTION_STRESS_MANAGEMENT: {
    HEADER: "Stresshantering",
    TEXT: "EQ hjälper dig att hantera stress när du står inför svåra beslut. I stället för att låta stressen ta över försöker du lugna dig och tänka igenom situationen. Du tar ett steg tillbaka, andas och ser på problemet med ett klart sinne. Det gör att du kan fatta bättre beslut utan att låta stressen styra.",
  },
  SUMMARY_HEADER: "Sammanfattning",
  SUMMARY:
    "EQ hjälper dig att fatta bättre beslut genom att du förstår både dina egna och andras känslor, hanterar dina reaktioner, håller dig motiverad, bygger starka relationer och håller stressen under kontroll. Det gör att dina beslut blir genomtänkta, rättvisa och hållbara i längden.",
};

const SESSION_EXPIRED_MESSAGE = "Ingen tillgång till resultaten";
const DATA_FETCH_ERROR_MESSAGE = "Fel vid hämtning av resultat";

const ResultPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const [timeValue, setTimeValue] = useState("1 Månad");
  const [period, setPeriod] = useState(1);
  const [periodValue, setPeriodValue] = useState("month");
  const [selectedResult, setSelectedResult] = useState("Stress");
  const [chartExplanation, setChartExplanation] = useState<
    ChartExplanationItem[]
  >([]);

  const [isLoadingChartData, setLoadingChartData] = useState(false);
  const [isSpinnerVisible, setSpinnerVisible] = useState(false);
  const [erorChart, setErrorChart] = useState("");

  const imageRef = useRef<HTMLImageElement>(null);
  const referenceElementRef = useRef<HTMLDivElement>(null);
  const imageRef2 = useRef<HTMLImageElement>(null);
  const referenceElementRef2 = useRef<HTMLParagraphElement>(null);
  const imageRef3 = useRef<HTMLImageElement>(null);
  const referenceElementRef3 = useRef<HTMLParagraphElement>(null);
  const imageRef4 = useRef<HTMLImageElement>(null);
  const referenceElementRef4 = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const setPosition = () => {
      if (imageRef.current && referenceElementRef.current) {
        const referenceElementPosition =
          referenceElementRef.current.getBoundingClientRect();
        const newTopPosition = referenceElementPosition.top + window.scrollY;

        imageRef.current.style.position = "absolute";
        imageRef.current.style.top = `${newTopPosition}px`;
        imageRef.current.style.left = `0px`;
      }

      if (imageRef2.current && referenceElementRef2.current) {
        const referenceElementPosition =
          referenceElementRef2.current.getBoundingClientRect();
        const newTopPosition = referenceElementPosition.top + window.scrollY;

        imageRef2.current.style.position = "absolute";
        imageRef2.current.style.top = `${newTopPosition}px`;
        imageRef2.current.style.right = `0px`;
      }

      if (imageRef3.current && referenceElementRef3.current) {
        const referenceElementPosition =
          referenceElementRef3.current.getBoundingClientRect();
        const newTopPosition = referenceElementPosition.top + window.scrollY;

        imageRef3.current.style.position = "absolute";
        imageRef3.current.style.top = `${newTopPosition}px`;
        imageRef3.current.style.left = `0px`;
      }

      if (imageRef4.current && referenceElementRef4.current) {
        const referenceElementPosition =
          referenceElementRef4.current.getBoundingClientRect();
        const newTopPosition = referenceElementPosition.top + window.scrollY;

        imageRef4.current.style.position = "absolute";
        imageRef4.current.style.top = `${newTopPosition}px`;
        imageRef4.current.style.right = `0px`;
      }
    };

    setPosition();
    window.addEventListener("resize", setPosition);

    return () => window.removeEventListener("resize", setPosition);
  });

  // Retrieve isn from localStorage
  const isn = localStorage.getItem("isn");

  // Use the custom hook to get test results
  const { data, isLoading } = useGetTestResults(isn as string); // Ensure isn is not null

  const [result, setResult] = useState<{
    totalEq: number;
    whiteWashEq: number;
    whiteWashExplain: string;
    stressEq: number;
    relationEq: number;
    motivationEq: number;
  } | null>(null);

  useEffect(() => {
    if (!isn) {
      setError(SESSION_EXPIRED_MESSAGE);
    } else if (data) {
      setResult(data.data);
    } else if (!data && !isLoading) {
      setError(DATA_FETCH_ERROR_MESSAGE);
    }
  }, [isn, data]);

  const points = result
    ? [
        { label: "Total EQ", value: result.totalEq, color: "#40C4FF" },
        { label: "Stress", value: result.stressEq, color: "#FFAB40" },
        { label: "Motivation", value: result.motivationEq, color: "#FFEA00" },
        { label: "Empati", value: result.relationEq, color: "#00E676" },
      ]
    : [];

  const handlePeriodChange = (selectedOption: any) => {
    setTimeValue(selectedOption.value);
    switch (selectedOption.value) {
      case "1 Månad":
        setPeriod(1);
        setPeriodValue("month");
        break;
      case "1 År":
        setPeriod(1);
        setPeriodValue("year");
        break;
      case "5 År":
        setPeriod(5);
        setPeriodValue("year");
        break;
      default:
        setPeriod(1);
        setPeriodValue("month");
    }
  };

  const handleResultChange = (selectedOption: any) => {
    setSelectedResult(selectedOption.value);
  };

  const stripHtmlTagsToString = (input: string): string => {
    // Replace <p> and <br> tags with newlines
    let cleanText = input.replace(/<\s*p[^>]*>/gi, "\n"); // Replace <p> tags with newlines
    cleanText = cleanText.replace(/<\s*br\s*\/?\s*>/gi, "\n"); // Replace <br> tags with newlines
    cleanText = cleanText.replace(/&nbsp;/gi, " "); // Replace non-breaking space entities with spaces
    cleanText = cleanText.replace(/<[^>]+>/g, ""); // Remove all other HTML tags

    // Remove extra newlines and trim text
    cleanText = cleanText.replace(/\n+/g, "\n").trim();

    return cleanText;
  };

  const resultDescriptions: { [key: string]: string | JSX.Element } = {
    Stress:
      "Vad är stress? Krav från omgivningen påverkar din kropp och du hanterar dessa krav med stress. Stressen hjälper dig att förstå varifrån kraven kommer och hur du kan hantera dem.",
    Motivation: (
      <>
        Vad är motivation? Motivation är när man verkligen vill göra något och
        känner starka känslor (intentioner) för målet. Motivation handlar om att
        sätta mål och använda sina målsättningar att nå dem. Det betyder att:
        <ul>
          <li>a) Bestämma vad man vill göra,</li>
          <li>b) Hur mycket man behöver jobba för det,</li>
          <li>c) Hålla igång och inte ge upp.</li>
        </ul>
      </>
    ),
    Empati:
      "Vad är Empati? Empati är viktigt för hur jag pratar och är med andra människor. Det handlar om att använda mina känslor för att förstå och bry sig om andra. När du tänker på hur du förstår och känner för andra, påverkar det hur du känner och hur du beter dig. Att tänka på andra tillsammans med dem påverkar dina känslor och hur du är och accepterar din omgivning.",
  };

  const { getHomeChartExplanation } = useChart();
  const { resultPdf, printResultPdf } = useTestMutation();

  const handleDownloadPdf = async () => {
    if (isn) {
      setIsDownloading(true);
      try {
        await resultPdf.trigger(isn);
      } catch (error) {
        toast.error("Fel vid nedladdning av filen", {
          style: {
            backgroundColor: "#3F51B5",
          },
        });
      } finally {
        setIsDownloading(false);
      }
    } else {
      toast.error("Fel vid nedladdning av filen", {
        style: {
          backgroundColor: "#3F51B5",
        },
      });
    }
  };

  useEffect(() => {
    if (result && periodValue && period) {
      setLoadingChartData(true);
      setSpinnerVisible(true);

      let selectedValue: number | null = null;
      switch (selectedResult) {
        case "Stress":
          selectedValue = result.stressEq;
          break;
        case "Motivation":
          selectedValue = result.motivationEq;
          break;
        case "Empati":
          selectedValue = result.relationEq;
          break;
        default:
          selectedValue = result.totalEq;
      }

      if (selectedValue !== null) {
        const timerPromise = new Promise<void>((resolve) => {
          setTimeout(resolve, 500);
        });

        const requestPromise = getHomeChartExplanation.trigger({
          type: selectedResult,
          value: selectedValue,
          period,
          periodValue,
        });

        Promise.all([timerPromise, requestPromise])
          .then(([_, res]) => {
            const chartData = res.data?.data || [];
            const explanations = chartData.map((item: any) => ({
              reason: item.reason,
              percent: item.percent,
            }));
            setChartExplanation(explanations || []);
            setErrorChart("");
          })
          .catch(() => {
            setErrorChart(CHART_ERROR_MSG);
            toast.error(CHART_ERROR_MSG, {
              style: {
                backgroundColor: "#3F51B5",
              },
            });
          })
          .finally(() => {
            setLoadingChartData(false);
            setSpinnerVisible(false);
          });
      }
    }
  }, [selectedResult, periodValue, period, timeValue, result]);

  const handlePrintPdf = async () => {
    if (isn) {
      try {
        await printResultPdf.trigger(isn);
      } catch (error) {
        toast.error("Ett fel uppstod vid utskrift", {
          style: {
            backgroundColor: "#3F51B5",
          },
        });
      }
    } else {
      toast.error("Ett fel uppstod vid utskrift", {
        style: {
          backgroundColor: "#3F51B5",
        },
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center w-full">
        <CustomSpinner />
      </div>
    );
  }

  if (error) {
    return <ErrorPage message={error} />;
  }

  return (
    <main className="flex-grow h-full flex items-center justify-center">
      <div className="flex flex-col gap-10 lg:max-w-[850px] mb-10">
        <HurSection />
        <div className="flex flex-col justify-center items-center">
          <div className="w-full h-full bg-primary-card rounded-lg p-4 text-start max-w-[1260px] mx-3 md:py-8 md:px-10 font-nunito flex flex-col gap-5 mb-2">
            <div className="flex justify-between">
              <h2 className="faq-header text-white text-2xl md:text-3xl lg:text-4xl font-semibold">
                {TEXT.HEADER}
                {result?.totalEq}
              </h2>
              <div className="flex gap-4">
                {/* <button
                onClick={handleDownloadPdf}
                className={classNames(
                  "text-white transform transition duration-300 hover:scale-105 active:scale-100 hover:shadow-lg active:shadow-inner",
                  { "opacity-50 cursor-not-allowed": isDownloading }
                )}
                disabled={isDownloading}
              >
                <img src={downloadIcon} alt="download" className="w-8 h-6" />
              </button> */}

                <button
                  onClick={handlePrintPdf}
                  className="text-white transform transition duration-300 hover:scale-105 active:scale-100 hover:shadow-lg active:shadow-inner"
                >
                  <img src={printIcon} alt="print" className="w-8 h-8" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-8 lg:gap-[50px] w-full h-full">
              <div>
                <p className="text-center text-lg text-white bg-primary-card font-quicksand">
                  Normalfördelning av EQ Skala
                </p>
                <div className="w-full flex items-center justify-center">
                  <DynamicChart points={points} />
                </div>
                <div className="flex justify-between gap-3 w-full mt-4">
                  <CustomSelect
                    title={resultDescriptions[selectedResult]}
                    options={SELECT_OPTIONS.RESULT}
                    label={"EQ"}
                    onChange={handleResultChange}
                    placement="bottomRight"
                  />
                  <CustomSelect
                    title={
                      "Reaktionsmönstret varierar i med tiden och rummet där du tar dina beslut."
                    }
                    options={SELECT_OPTIONS.TIME}
                    label={"Tid"}
                    onChange={handlePeriodChange}
                    placement="bottom"
                  />
                </div>
              </div>
              <div className="w-full h-full flex flex-col justify-center gap-6 text-start mt-4">
                <div className="flex justify-center">
                  <div
                    className="flex justify-between gap-4"
                    ref={referenceElementRef}
                  >
                    <h5 className="text-white text-xl md:text-3xl font-nunito text-center">
                      {"Resultat för olika tidsperioder"}
                    </h5>
                  </div>
                </div>
                <div
                  className={classNames("relative", {
                    "sm:min-h-[100px] lg:min-h-[100px]": !!erorChart,
                  })}
                >
                  {(isLoadingChartData || isSpinnerVisible) && (
                    <CustomSpinner />
                  )}
                  {chartExplanation.length > 0 && (
                    <ul
                      className={`list-none space-y-6 mt-4 ${
                        isLoadingChartData ? "opacity-30" : ""
                      }`}
                    >
                      {chartExplanation.map((item, index) => (
                        <li key={index} className="flex items-center">
                          <div
                            className="w-[40px] h-[40px] p-3 flex items-center justify-center text-white rounded-lg mr-4"
                            style={{
                              backgroundColor:
                                item.percent === 20
                                  ? "#2E8260"
                                  : item.percent === 10
                                  ? "#34A16E"
                                  : item.percent === -10
                                  ? "#B5493F"
                                  : item.percent === -20
                                  ? "#8F403C"
                                  : "#3F51B5", // default color if no match
                            }}
                          >
                            <p className="text-white font-quicksand text-[13px] font-normal leading-[24px]">
                              {item.percent > 0 ? "+" : ""}
                              {item.percent}%
                            </p>
                          </div>
                          <p className="text-white font-quicksand text-[16px] font-normal leading-[24px]">
                            {item.reason}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="my-3 mt-10 flex items-center justify-center w-full">
                    <button
                      onClick={handleDownloadPdf}
                      className={classNames(
                        "flex justify-center items-center gap-2 px-9 py-3 rounded-lg transition duration-150 ease-in-out transform ",
                        "bg-[#3F51B5] text-white border-0 hover:bg-[#303F9F] active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#303F9F]",
                        { "opacity-50 cursor-not-allowed": isDownloading }
                      )}
                      disabled={isDownloading}
                    >
                      <img
                        src={downloadIcon}
                        alt="download"
                        className="w-5 h-4"
                      />
                      {"Ladda ner ditt testresultat"}
                    </button>
                  </div>
                  {erorChart && (
                    <div
                      className={`w-full h-full flex items-start justify-center ${
                        isLoadingChartData ? "opacity-30" : ""
                      }`}
                    >
                      <p className="text-white font-nunito text-[28px] font-normal leading-[24px] text-center">
                        {erorChart}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full h-full bg-primary-card rounded-lg p-4 text-start max-w-[1260px] mx-3 md:py-8 md:px-10 flex flex-col gap-3 my-10 mb-2 font-quicksand">
            <h2 className="text-white text-[28px]">{TEXTS.TITLE}</h2>
            {!!result?.whiteWashExplain && (
              <p className="text-white text-[16px] font-normal leading-[24px]">
                {stripHtmlTagsToString(result?.whiteWashExplain)}
              </p>
            )}
            <p className="text-white text-[16px] font-normal leading-[24px]">
              {TEXTS.PARAGRAPH_1}
            </p>

            <h3 className="text-white text-[16px] font-bold mt-4">
              {TEXTS.HEADER_MOTIVATION}
            </h3>
            <p className="text-white text-[16px] font-normal leading-[24px]">
              {TEXTS.PARAGRAPH_2}
            </p>
            <p
              className="text-white text-[16px] font-normal leading-[24px]"
              ref={referenceElementRef2}
            >
              {TEXTS.PARAGRAPH_3}
            </p>

            <h3 className="text-white text-[16px] font-bold mt-4">
              {TEXTS.HEADER_EMPATHY}
            </h3>
            <p className="text-white text-[16px] font-normal leading-[24px]">
              {TEXTS.PARAGRAPH_4}
            </p>
            <p className="text-white text-[16px] font-normal leading-[24px]">
              {TEXTS.PARAGRAPH_5}
            </p>

            <h3 className="text-white text-[16px] font-bold mt-4">
              {TEXTS.HEADER_STRESS}
            </h3>
            <p className="text-white text-[16px] font-normal leading-[24px]">
              {TEXTS.PARAGRAPH_6}
            </p>
            <p className="text-white text-[16px] font-normal leading-[24px]">
              {TEXTS.PARAGRAPH_7}
            </p>

            <h3 className="text-white text-[16px] font-bold mt-4">
              {TEXTS.SUMMARY_HEADER}
            </h3>
            <p className="text-white text-[16px] font-normal leading-[24px]">
              {TEXTS.SUMMARY}
            </p>
          </div>

          <div className="w-full h-full bg-primary-card rounded-lg p-4 text-start max-w-[1260px] mx-3 md:py-8 md:px-10 flex flex-col gap-3 my-10 mb-[50px] font-quicksand">
            <h2 className="text-white text-[28px] ">{EQ_TEXTS.TITLE}</h2>
            <h3 className="text-white text-[16px] font-bold mt-4">
              {EQ_TEXTS.HEADER_DECISION}
            </h3>
            <p
              className="text-white text-[16px] font-normal leading-[24px]"
              ref={referenceElementRef3}
            >
              {EQ_TEXTS.INTRODUCTION}
            </p>

            {/* Section: Self-Awareness */}
            <h4 className="text-white text-[16px] font-bold mt-4">
              {EQ_TEXTS.SECTION_SELF_AWARENESS.HEADER}
            </h4>
            <p className="text-white text-[16px] font-normal leading-[24px]">
              {EQ_TEXTS.SECTION_SELF_AWARENESS.TEXT}
            </p>

            {/* Section: Self-Regulation */}
            <h4 className="text-white text-[16px] font-bold mt-4">
              {EQ_TEXTS.SECTION_SELF_REGULATION.HEADER}
            </h4>
            <p className="text-white text-[16px] font-normal leading-[24px]">
              {EQ_TEXTS.SECTION_SELF_REGULATION.TEXT}
            </p>

            {/* Section: Empathy */}
            <h4 className="text-white text-[16px] font-bold mt-4">
              {EQ_TEXTS.SECTION_EMPATHY.HEADER}
            </h4>
            <p className="text-white text-[16px] font-normal leading-[24px]">
              {EQ_TEXTS.SECTION_EMPATHY.TEXT}
            </p>

            {/* Section: Motivation */}
            <h4 className="text-white text-[16px] font-bold mt-4">
              {EQ_TEXTS.SECTION_MOTIVATION.HEADER}
            </h4>
            <p
              className="text-white text-[16px] font-normal leading-[24px]"
              ref={referenceElementRef4}
            >
              {EQ_TEXTS.SECTION_MOTIVATION.TEXT}
            </p>

            {/* Section: Social Skills */}
            <h4 className="text-white text-[16px] font-bold mt-4">
              {EQ_TEXTS.SECTION_SOCIAL_SKILLS.HEADER}
            </h4>
            <p className="text-white text-[16px] font-normal leading-[24px]">
              {EQ_TEXTS.SECTION_SOCIAL_SKILLS.TEXT}
            </p>

            {/* Section: Stress Management */}
            <h4 className="text-white text-[16px] font-bold mt-4">
              {EQ_TEXTS.SECTION_STRESS_MANAGEMENT.HEADER}
            </h4>
            <p className="text-white text-[16px] font-normal leading-[24px]">
              {EQ_TEXTS.SECTION_STRESS_MANAGEMENT.TEXT}
            </p>

            {/* Summary */}
            <h3 className="text-white text-[16px] font-bold mt-4">
              {EQ_TEXTS.SUMMARY_HEADER}
            </h3>
            <p className="text-white text-[16px] font-normal leading-[24px]">
              {EQ_TEXTS.SUMMARY}
            </p>
          </div>

          <img
            src={circle}
            alt="main-photo"
            className="absolute bottom-0 right-0 w-[230px] h-[230px] md:w-[400px] md:h-[400px] -z-10"
          />
          <img
            ref={imageRef}
            src={circle104}
            alt="circle104"
            className="object-cover w-[230] h-[230px] md:w-[800] md:h-[800px] -z-10"
          />
          <img
            ref={imageRef2}
            src={circle107}
            alt="circle104"
            className="object-cover w-[230] h-[230px] md:w-[800] md:h-[800px] -z-10"
          />
          <img
            ref={imageRef3}
            src={circle106}
            alt="circle104"
            className="object-cover w-[230] h-[230px] md:w-[400] md:h-[400px] -z-10"
          />
          <img
            ref={imageRef4}
            src={circle108}
            alt="circle104"
            className="object-cover w-[230] h-[230px] md:w-[600] md:h-[600px] -z-10"
          />
        </div>
      </div>
    </main>
  );
};

export default ResultPage;
